// ════════════════════════════════════════════════════════════════
// generatePayloadForScreen.js
// Centralized pure function that builds the Myra payload for each
// GSA screen. Takes (screenName, assessmentData), returns payload.
// ════════════════════════════════════════════════════════════════

import {
  MESSAGING,
  TEST_MAP,
  TEST_PLAIN_DESCRIPTIONS,
  getNegativeTest,
  CLINICAL_PATHS,
  CPG_OPTIONS_LEG,
  CPG_OPTIONS_BACK,
  Q1_OPTIONS,
  Q2_MAP,
  hasLegPain,
  resolveLongTermBeatKey,
  resolveMovementTestNameFromAssessment,
} from './GSA_CONSTANTS';
import { MYRA_MESSAGES } from './GSA_MYRA_MESSAGES';
import MyraInteraction from "./MyraInteractionController";

export const PLAN_GROUP1_BUBBLE_1_FALLBACK = "You did it — and that matters more than you might think. Let me share what I found and what your plan is built on.";
export const PLAN_GROUP1_BUBBLE_2_FALLBACK = "Ask me anything about what to expect — or type continue when you're ready to see your plan.";
export const PLAN_GROUP2_CLOSING = "Ask me about any of these whenever you like. When you're ready, let me know and I'll share how I can help with your recovery.";
export const PLAN_GROUP3_BUBBLE_1_FALLBACK = "Your plan starts with what your body needs today.";
export const PLAN_GROUP3_BUBBLE_2_FALLBACK = "Here is how I help you keep moving forward. If something feels off along the way, I am right here to adjust it for you.";
export const PLAN_PRICING_BUBBLE = "Start for $6.99 your first week. Then $10.99 a week. Cancel anytime.<br><br>Prefer to commit to the full journey? $68 for your 8-week plan.";
// ── Helpers ──────────────────────────────────────────────────

export function getUserConditions() {
  try {
    const raw = localStorage.getItem('userCondition');
    if (!raw) return { pre_existing: [], current: [] };
    const parsed = JSON.parse(raw);
    return parsed.usercondition || { pre_existing: [], current: [] };
  } catch {
    return { pre_existing: [], current: [] };
  }
}

export function parseDataAfterInfo() {
  try {
    let raw = localStorage.getItem('dataAfterInfo');
    if (!raw) return { rom: [], dur: [] };
    let parsed = JSON.parse(raw);
    if (typeof parsed === 'string') parsed = JSON.parse(parsed);
    const rom = parsed.map(rep => rep[0]);
    const dur = parsed.map(rep => rep[3]);
    return { rom, dur };
  } catch {
    return { rom: [], dur: [] };
  }
}

function getInputMode() {
  return MyraInteraction.getMode() || 'button';
}

function buildBase(screenName) {
  const base = {
    gsa_step: screenName,
    user_name: localStorage.getItem('self_name') || '',
    input_mode: getInputMode(),
    user_conditions: getUserConditions(),
  };
  const landingChat = window.landing_chat || [];
  const landingHistory = window.landingHistory || [];

  const normalizeMessages = (messages = []) =>
    messages.map((msg) => ({
      role: msg.role === "model" ? "assistant" : msg.role,
      content: msg.content || msg.text || "",
    }));

  const normalizedLandingChat = normalizeMessages(landingChat);
  const normalizedLandingHistory = normalizeMessages(landingHistory);

  let finalLandingChat = [];

  // if both exist → merge them
  if (normalizedLandingChat.length && normalizedLandingHistory.length) {
    console.log(
      "BOTH LANDING CHAT + LANDING HISTORY FOUND, MERGING:",
      normalizedLandingChat,
      normalizedLandingHistory
    );

    finalLandingChat = [
      ...normalizedLandingHistory,
      ...normalizedLandingChat,
    ];
  }

  // only landing_chat exists
  else if (normalizedLandingChat.length) {
    console.log(
      "ONLY LANDING CHAT FOUND:",
      normalizedLandingChat
    );

    finalLandingChat = normalizedLandingChat;
  }

  // only landingHistory exists
  else if (normalizedLandingHistory.length) {
    console.log(
      "ONLY LANDING HISTORY FOUND:",
      normalizedLandingHistory
    );

    finalLandingChat = normalizedLandingHistory;
  }

  if (finalLandingChat.length) {
    base.landing_chat = finalLandingChat;
  }
  return base;
}

function buildCurrentUnderstandingBase(assessmentData) {
  return {
    pain_locations: assessmentData.selectedZones || [],
  };
}

export function buildCurrentUnderstandingFull(assessmentData) {
  const subtype = assessmentData.probableSubtype || assessmentData.subtype;
  const diagnosis = subtype ? MESSAGING.diagnosis[subtype] : null;

  return {
    pain_locations: assessmentData.selectedZones || [],
    relief_factors: diagnosis?.relief || null,
    perceived_subtype: subtype || null,
    pattern_description: diagnosis?.implications || null,
  };
}

function buildTest1Details(assessmentData) {
  const subtype = assessmentData.probableSubtype || assessmentData.subtype || 'NEUTRAL_SENSITIVE';
  const test = TEST_MAP[subtype];
  if (!test) return [];
  return [{
    name: test.name,
    subtype: subtype,
    video: test.video,
    testNumber: 1,
  }];
}


export function buildMovementBucket(assessmentData = {}, includeComparison = false) {
  const data = assessmentData || {};
  const bucket = {
    rpe: (typeof data.rpe === "number") ? data.rpe : null,
    feelAffect: data.feelAffect || data.symptomResponse || null,
    tempo: data.selfReportTempo || data.selfReportSpeed || data.selfReportPace || data.tempo || null,
    rom: data.selfReportRom || data.selfReportRange || null,
  };
  // comparison is a conditional fifth field. The key is only present when the
  // frontend is on the movement comparison step; its presence is the switch
  // the movement prompt keys on. Never add it unconditionally.
  if (includeComparison) {
    bucket.comparison = data.symptomResponseConfirm || null;
  }
  return bucket;
}

export function buildMovementQuestionBank() {
  return {
    q1_effort_symptoms: {
      fields: ["rpe", "feelAffect"],
      setupBeat: MYRA_MESSAGES.movementQuestions?.q1?.setupBeat || "",
      effort: {
        field: "rpe",
        dimension: "how challenging the movement felt, separate from pain",
        nugget: MYRA_MESSAGES.rpe?.RPE_NUGGET || "",
        scale: Object.entries(MYRA_MESSAGES.rpe?.labelByValue || {}).map(([value, label]) => ({
          value: Number(value),
          label,
        })),
      },
      symptoms: {
        field: "feelAffect",
        dimension: "what the movement did to their symptoms",
        outcomes: [
          { value: "IMPROVED", plain: MYRA_MESSAGES.feelAffect?.options?.["symptom-improved"]?.label || "better" },
          { value: "TOLERABLE", plain: MYRA_MESSAGES.feelAffect?.options?.["symptom-tolerable"]?.label || "the same" },
          { value: "WORSENED", plain: MYRA_MESSAGES.feelAffect?.options?.["symptom-worsened"]?.label || "worse" },
        ],
      },
    },
    q2_rep_consistency: {
      fields: ["tempo", "rom"],
      setupBeat: MYRA_MESSAGES.movementQuestions?.q2?.setupBeat || "",
      tempo: {
        field: "tempo",
        dimension: "whether they kept up the pace across all the reps",
        nugget: MYRA_MESSAGES.selfReportTempo?.SELF_REPORT_TEMPO_NUGGET_BY_AREA || {},
        outcomes: Object.entries(MYRA_MESSAGES.selfReportTempo?.options || {}).map(([value, copy]) => ({
          value,
          plain: copy?.title || value,
          hardStop: value === "tempo_not_finished",
        })),
      },
      range: {
        field: "rom",
        dimension: "whether their movements stayed as full from first reps to last",
        forbiddenPhrasing: ["range of motion", "ROM"],
        outcomes: Object.entries(MYRA_MESSAGES.selfReportRom?.options || {}).map(([value, copy]) => ({
          value,
          plain: copy?.title || value,
        })),
      },
    },
  };
}

const MOVEMENT_FIELD_ORDER = ["rpe", "feelAffect", "tempo", "rom"];

export function lowestEmptyMovementField(bucket = {}) {
  for (const field of MOVEMENT_FIELD_ORDER) {
    if (field === "rom" && bucket.tempo === "tempo_not_finished") continue;
    if (bucket[field] === null || bucket[field] === undefined) return field;
  }
  // comparison is checked fifth, only when its key is present in the bucket.
  if (Object.prototype.hasOwnProperty.call(bucket, "comparison") && (bucket.comparison === null || bucket.comparison === undefined)) {
    return "comparison";
  }
  return null;
}

export function movementTurnIntentForField(field) {
  switch (field) {
    case "rpe": return "movement_rpe";
    case "feelAffect": return "movement_feelAffect";
    case "tempo": return "movement_tempo";
    case "rom": return "movement_rom";
    default: return null;
  }
}

function buildTest2Details(assessmentData) {
  const subtype = assessmentData.probableSubtype || assessmentData.subtype || 'NEUTRAL_SENSITIVE';
  const problemArea = assessmentData.problemArea;
  const test2 = getNegativeTest(subtype, problemArea);
  if (!test2) return [];
  return [{
    name: test2.name,
    subtype: subtype,
    video: test2.video,
    testNumber: 2,
  }];
}

function buildTestResults1() {
  const data = parseDataAfterInfo();
  return {
    movement_test_1: {
      rom: data.rom,
      dur: data.dur,
    },
  };
}

function buildTestResults2(assessmentData) {
  const test1Results = buildTestResults1();
  return {
    ...test1Results,
    movement_test_2: {
      feedback2: assessmentData.test2?.feedback2 || null,
      skipped: assessmentData.test2?.skipped || false,
    },
  };
}

// ── Intro payload helpers (Spec 1) ──────────────────────────

// bucket_state — current values of the seven core GSA fields the bucket
// router uses to decide where to land when the user is ready.
export function buildBucketState(assessmentData = {}) {
  const data = assessmentData || {};
  return {
    problemArea: data.problemArea || null,
    selectedZones: (data.selectedZones && data.selectedZones.length) ? data.selectedZones : (data.pendingSelectedZones && data.pendingSelectedZones.length) ? data.pendingSelectedZones : null,
    side: data.side || null,
    cpgAnswer: data.cpgAnswer || null,
    subtypeQ1: data.subtypeQ1 || null,
    subtypeQ2: data.subtypeQ2 || null,
    subtypeAnswer: data.subtypeAnswer || null,
    subtype: data.subtype || null,
    probableSubtype: data.probableSubtype || null,
    painLevel: (typeof data.painLevel === "number") ? data.painLevel : null,
    reveal: data.reveal || null,
    safety: data.safety || null,
  };
}

// personalize_state — current values of fields that the Personalize screen
// would otherwise collect. Awareness only: tells Claude what's already
// captured so she doesn't re-ask. Not a queue.
//
// Dual reads on weight / activity / duration: Claude's extraction emits
// weightLbs / activityLevel / symptomDuration; the Personalize screen's
// local form-state uses weight / activity / duration. Either name landing
// in assessmentData should resolve to the same canonical value.
export function buildPersonalizeState(assessmentData = {}) {
  const data = assessmentData || {};
  const validSymptomDurations = ["ACUTE", "SUBACUTE", "CHRONIC"];
  const symptomDuration = validSymptomDurations.includes(data.symptomDuration) ? data.symptomDuration : validSymptomDurations.includes(data.duration) ? data.duration : null;
  return {
    age: data.age || null,
    weightLbs: data.weightLbs || data.weight || null,
    heightFt: data.heightFt || null,
    heightIn: data.heightIn || null,
    activityLevel: data.activityLevel || data.activity || null,
    symptomDuration,
    balance: data.balance || null,
    floorCapability: data.floorCapability || null,
    goals: (data.goals && Array.isArray(data.goals) && data.goals.length) ? data.goals : null,
  };
}

// ── Internal: resolve area-specific CPG options/question/subtype index ─

// Returns the array of CPG options that apply for the user's area.
// LBP branches by leg-pain presence; HKT pulls from CLINICAL_PATHS.
function areaCpgOptions(area, selectedZones) {
  if (area === "LBP") {
    return hasLegPain(selectedZones || []) ? CPG_OPTIONS_LEG : CPG_OPTIONS_BACK;
  }
  return CLINICAL_PATHS[area]?.cpgQuestion?.options || [];
}

// Returns the CPG question text for the user's area. Per D2 in the
// implementation brief, HKT uses cpg.questionByArea (matching the live
// CPGQuestionScreen at SelfAssessment.jsx line 2647). LBP uses the
// leg/back question pair already in MYRA_MESSAGES.cpg.
function areaCpgQuestion(area, selectedZones) {
  if (area === "LBP") {
    return hasLegPain(selectedZones || [])
      ? MYRA_MESSAGES.cpg?.lbpLegQuestion
      : MYRA_MESSAGES.cpg?.lbpBackQuestion;
  }
  return MYRA_MESSAGES.cpg?.questionByArea?.[area] || CLINICAL_PATHS[area]?.cpgQuestion?.prompt || "";
}

// For HKT areas, returns the index (0 or 1) of the subtype question that
// the chosen CPG option routes to. Returns null when:
//   - area is LBP (uses Q1/Q2 instead)
//   - cpgAnswerId is missing
//   - the CPG option routes "direct" (subtype already determined)
function getHktSubtypeQuestionIndex(area, cpgAnswerId) {
  if (!area || area === "LBP" || !cpgAnswerId) return null;
  const options = CLINICAL_PATHS[area]?.cpgQuestion?.options || [];
  const opt = options.find((o) => o.id === cpgAnswerId);
  if (!opt) return null;
  if (opt.routesTo === "direct") return null;
  return opt.subtypeQuestionIndex ?? 0;
}

// bridge — copy + test-name needed to hand off into the movement check once
// enough is known to pick a test. Null until area + subtype + a long-term
// beat key all resolve.
function buildBridgeBlock(assessmentData = {}) {
  const data = assessmentData || {};
  const area = data.problemArea;
  if (!area) return null;

  const subtype = data.probableSubtype || data.subtype;
  if (!subtype) return null;

  const beatKey = resolveLongTermBeatKey(data);
  if (!beatKey) return null;

  const [, subPattern] = beatKey.split(".");
  const q1Fired = area === "LBP" ? !!data.subtypeQ1 : !!data.subtypeAnswer;
  const trustCloseKey = `${area}_${q1Fired ? "q1Fired" : "q1Skipped"}`;


  const bridgeBlock = {
    trustClose: MYRA_MESSAGES.handoff?.trustCloseByFlow?.[trustCloseKey] || "",
    longTermBeat: MYRA_MESSAGES.handoff?.longTermBeats?.[area]?.[subPattern] || "",
    testPickTemplate: MYRA_MESSAGES.handoff?.testPickTemplate || "",
    movementRevealQuestion: MYRA_MESSAGES.handoff?.movementRevealQuestion || "",
    movementRevealReadyLabel: MYRA_MESSAGES.handoff?.movementRevealReadyLabel || "",
    testName: resolveMovementTestNameFromAssessment(data),
  };


  return bridgeBlock;
}

// gsa_question_bank — what to ask + how to ask it, per bucket field.
// Blocks fill in progressively as the bucket fills. Per D3, only painLevel
// carries a real nugget on the live screens. Per D5, selectedZones carries
// the full painLocation.validation map; Claude selects the right key by
// pattern. Per D10, no `personalize` block — those fields are exposed via
// personalize_state and user_conditions.
export function buildGsaQuestionBank(assessmentData = {}) {
  const data = assessmentData || {};
  const area = data.problemArea;
  const selectedZones = data.selectedZones || [];

  const bank = {
    problemArea: {
      field: "problemArea",
      values: ["LBP", "HIP", "THIGH", "KNEE"],
      valueLabels: MYRA_MESSAGES.problemArea?.optionsById || {},
      setupBeat: MYRA_MESSAGES.problemArea?.PROBLEM_AREA_SETUP_BEAT || "",
      question: MYRA_MESSAGES.problemArea?.PROBLEM_AREA_QUESTION || "",
      ackByArea: MYRA_MESSAGES.problemArea?.ackByArea || {},
      setupBeatByArea: MYRA_MESSAGES.problemArea?.setupBeatByArea || {},
    },

    selectedZones: area ? {
      field: "selectedZones",
      valueFormat: "side:zoneName",
      validZones: CLINICAL_PATHS[area]?.zones || { left: [], right: [] },
      question: MYRA_MESSAGES.painLocation?.questionByArea?.[area]?.question || "",
      validation: MYRA_MESSAGES.painLocation?.validation || {},
    } : null,

    side: area ? {
      field: "side",
      values: ["left", "right", "both"],
      question: MYRA_MESSAGES.side?.questionByArea?.[area] || "",
    } : null,

    cpgAnswer: area ? {
      field: "cpgAnswer",
      options: areaCpgOptions(area, selectedZones),
      setupBeat: MYRA_MESSAGES.cpg?.setupByArea?.[area] || "",
      question: areaCpgQuestion(area, selectedZones),
      postTap: MYRA_MESSAGES.cpg?.postTap || {},
    } : null,

    subtypeQ1: (area === "LBP" && data.cpgAnswer) ? {
      field: "subtypeQ1",
      values: ["a", "b", "c", "d", "e"],
      options: Q1_OPTIONS,
      setupBeat: MYRA_MESSAGES.subtypeQ1?.setupByArea?.LBP || "",
      question: MYRA_MESSAGES.subtypeQ1?.lbpQuestion || "",
      nugget: MYRA_MESSAGES.subtypeQ1?.lbpNugget || "",
      postTap: MYRA_MESSAGES.subtypeQ1?.postTap || {},
    } : null,

    subtypeQ2: (area === "LBP" && (data.cpgAnswer || data.subtypeQ1)) ? {
      field: "subtypeQ2",
      values: ["yes", "no"],
      setupBeat: MYRA_MESSAGES.q2?.transition || "",
      question: MYRA_MESSAGES.q2?.titleByQ1Answer?.[data.subtypeQ1] || "",
      questionBySubtypeQ1: Object.keys(Q2_MAP).reduce((acc, key) => {
        acc[key] = Q2_MAP[key].title;
        return acc;
      }, {}),
      nugget: MYRA_MESSAGES.q2?.nuggetByQ1?.[data.subtypeQ1] || "",
      nuggetBySubtypeQ1: MYRA_MESSAGES.q2?.nuggetByQ1 || {},
      postTap: MYRA_MESSAGES.q2?.postTap || "",
    } : null,

    // HKT subtype — populated below only when the CPG option routes through
    // a subtype question (otherwise null).
    subtypeAnswer: null,

    painLevel: {
      field: "painLevel",
      range: [0, 10],
      setupBeat: MYRA_MESSAGES.preNprs?.NPRS_SETUP_BEAT || "",
      question: MYRA_MESSAGES.preNprs?.painPrompt || "",
      nugget: MYRA_MESSAGES.preNprs?.nugget || "",
      postTap: MYRA_MESSAGES.preNprs?.NPRS_POST_TAP_NUGGET || "",
    },

    safety: {
      field: "safety",
      setupBeat: MYRA_MESSAGES.safety?.SAFETY_SETUP_BEAT || "",
      question: MYRA_MESSAGES.safety?.SAFETY_QUESTION || "",
      nuggetByArea: MYRA_MESSAGES.safety?.SAFETY_NUGGET_BY_AREA || {},
    },

    bridge: buildBridgeBlock(data),
  };

  // HKT subtype block — only when the CPG option routes via a subtype question
  if (area && area !== "LBP" && data.cpgAnswer) {
    const idx = getHktSubtypeQuestionIndex(area, data.cpgAnswer);
    if (idx !== null) {
      const nuggetKey = `${area}_${idx}`;
      bank.subtypeAnswer = {
        field: "subtypeAnswer",
        options: CLINICAL_PATHS[area]?.subtypeQuestions?.[idx]?.options || [],
        setupBeat: MYRA_MESSAGES.subtypeQ1?.setupByArea?.[area] || "",
        question: MYRA_MESSAGES.hktSubtype?.promptByAreaAndIndex?.[nuggetKey] || "",
        nugget: MYRA_MESSAGES.subtypeQ1?.nuggetByAreaAndIndex?.[nuggetKey] || "",
        postTap: MYRA_MESSAGES.subtypeQ1?.postTap || {},
      };
    }
  }

  return bank;
}

// source_site — used by the Landing prompt to tailor "where to click" copy
// (button below video on startfreept vs button at top of page on virdiohealth).
export function detectSourceSite() {
  const host = (typeof window !== "undefined" && window.location?.host) || "";
  if (host.includes("virdiohealth.com")) return "virdiohealth";
  if (host.includes("myptmyra.com") || host.includes("myptmyra")) return "myptmyra";
  return "myptmyra"; // default for startfreept.com, www.startfreept.com, and local/dev
}


// ── Main Export ──────────────────────────────────────────────

export function generatePayloadForScreen(screenName, assessmentData) {
  const base = buildBase(screenName);
  const data = assessmentData || {};
  const recoveryPlan = {
    ...(data.planDetails || {}),
    ...(data.recoveryPlan?.exercises?.length > 0
      ? { exercises: data.recoveryPlan.exercises }
      : {}),
  }

  switch (screenName) {

    // ── Intro (Spec 1: conversational intake on landing page) ──
    // The intro payload exposes bucket_state, personalize_state, and
    // gsa_question_bank for Claude to drive the bucket-aware conversation.
    // user_conditions and landing_chat already come from buildBase.
    case 'intro':
      return {
        ...base,
        source_site: detectSourceSite(),
        bucket_state: buildBucketState(data),
        personalize_state: buildPersonalizeState(data),
        gsa_question_bank: buildGsaQuestionBank(data),
      };

    // ── Intro / landing (pre-assessment) ──
    case 'landing':
      return base;

    // ── Early screens (base only) ──
    case 'safety_screen':
    case 'problem_area':
    case 'pain_location':
      return base;

    // ── Safety screening ──
    // Sends pain_locations so Claude has context, plus the user's
    // checked safety conditions (labels + IDs) when available.
    // safety_selections is empty [] on initial mount and populated
    // after the user submits the checklist.
    case 'SafetyScreeningScreen':
      return {
        ...base,
        current_understanding: {
          pain_locations: data.selectedZones || [],
        },
        safety_selections: data.safetySelections || [],
        safety_selection_ids: data.safetySelectionIds || [],
      };

    // ── CPG questions ──
    case 'cpg_questions':
      return {
        ...base,
        current_understanding: buildCurrentUnderstandingBase(data),
      };

    // ── Subtype, self-assessment, NPRS (full understanding) ──
    case 'subtype_questioning':
    case 'self_assessment':
    case 'nprs_symptoms':
      return {
        ...base,
        current_understanding: buildCurrentUnderstandingFull(data),
      };

    // ── Test 1 flow: demo, camera, movement, self-report, RPE ──
    case 'test1_demo':
    case 'camera_permission':
    case 'movement_test_1':
    case 'self_report_compare':
    case 'self_report_speed':
    case 'mode_selection':
    case 'test1_rpe':
      return {
        ...base,
        current_understanding: buildCurrentUnderstandingFull(data),
        current_test: buildTest1Details(data),
      };

    // ── Test 1 symptoms (adds test results) ──
    case 'test1_symptoms':
      return {
        ...base,
        current_understanding: buildCurrentUnderstandingFull(data),
        current_test: buildTest1Details(data),
        test_results: buildTestResults1(),
      };

    // ── Phase 2 movement questions: Q1 effort/symptoms, Q2 tempo/ROM ──
    case 'movement_q1':
    case 'movement_q2': {
      const movementBase = { ...base, gsa_step: "movement_test" };
      delete movementBase.user_conditions;
      return {
        ...movementBase,
        current_test: buildTest1Details(data).map(({ name, subtype }) => ({ name, subtype })),
        movement_bucket: buildMovementBucket(data),
        question_bank: buildMovementQuestionBank(),
        test_results: buildTestResults1(),
      };
    }

    // ── Movement comparison (conditional Q3, Feedback2 screen chat turns) ──
    // Movement-prompt payload shape with the comparison key in play. The
    // symptom2 case below stays untouched; it still serves the mid-journey
    // Q&A overlay on this screen.
    case 'movement_comparison': {
      const comparisonSubtype = data.probableSubtype || data.subtype || 'NEUTRAL_SENSITIVE';
      const comparisonArea = data.problemArea;
      const test1Name = TEST_MAP[comparisonSubtype]?.name || null;
      const test2Lookup = getNegativeTest(comparisonSubtype, comparisonArea);
      const test2Name = test2Lookup?.name || null;
      const test2Description = test2Name ? (TEST_PLAIN_DESCRIPTIONS?.[test2Name] || null) : null;
      const movementBase = { ...base, gsa_step: "movement_test" };
      delete movementBase.user_conditions;
      return {
        ...movementBase,
        current_test: [...buildTest1Details(data), ...buildTest2Details(data)].map(({ name, subtype }) => ({ name, subtype })),
        movement_bucket: buildMovementBucket(data, true),
        question_bank: buildMovementQuestionBank(),
        test_results: buildTestResults1(),
        test1Name,
        test2Description,
      };
    }

    // ── Symptom2 / Feedback2 (both tests) ──
    case 'symptom2': {
      const subtype = data.probableSubtype || data.subtype || 'NEUTRAL_SENSITIVE';
      const problemArea = data.problemArea;
      const test1Name = TEST_MAP[subtype]?.name || null;
      const test2Lookup = getNegativeTest(subtype, problemArea);
      const test2Name = test2Lookup?.name || null;
      const test2Description = test2Name ? (TEST_PLAIN_DESCRIPTIONS?.[test2Name] || null) : null;
      return {
        ...base,
        current_test: [...buildTest1Details(data), ...buildTest2Details(data)],
        test_results: buildTestResults2(data),
        test1Name,
        test2Name,
        test2Description,
      };
    }

    // ── Personalize ──
    case 'personalize':
      return {
        ...base,
        current_test: [...buildTest1Details(data), ...buildTest2Details(data)],
        test_results: buildTestResults2(data),
      };

    // ── Recovery Score (adds userProfile) ──
    case 'recovery_score':
      return {
        ...base,
        current_test: [...buildTest1Details(data), ...buildTest2Details(data)],
        test_results: buildTestResults2(data),
        userProfile: {
          ...(data.biometrics || {}),
          ...(data.signature || {}),
          balance: assessmentData?.balance || "steady"
        },
      };

    // ── Plan Screen (adds currentPlan) ──
    case 'plan_screen':
      return {
        ...base,
        current_test: [...buildTest1Details(data), ...buildTest2Details(data)],
        test_results: buildTestResults2(data),
        userProfile: {
          ...(data.biometrics || {}),
          ...(data.signature || {}),
          balance: assessmentData?.balance || "steady"
        },
        currentPlan: recoveryPlan || data.planDetails || null,
        current_group: data._planCurrentGroup || 'group_1',
        group1_bubble_1: data.group1_bubble_1 || PLAN_GROUP1_BUBBLE_1_FALLBACK,
        group1_bubble_2: data.group1_bubble_2 || PLAN_GROUP1_BUBBLE_2_FALLBACK,
        group2_closing: data.group2_closing || PLAN_GROUP2_CLOSING,
        group3_bubble_1: data.group3_bubble_1 || PLAN_GROUP3_BUBBLE_1_FALLBACK,
        group3_bubble_2: data.group3_bubble_2 || PLAN_GROUP3_BUBBLE_2_FALLBACK,
        pricing_bubble: data.pricing_bubble || PLAN_PRICING_BUBBLE,
        chat_history: data._planChatHistory || data.chat_history || data.chatHistory || [],
        internal_rationale: data.internal_rationale || null,
        recovery_timeline: data.recovery_timeline || null,
        input_mode: "chat"
      };

    default:
      return base;
  }
}
